/**
 * Patologías Hooks - TanStack Query hooks for patient pathology management
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPatologias,
  getPatologia,
  createPatologia,
  updatePatologia,
  deletePatologia,
  deactivatePatologia,
  activatePatologia,
  type Patologia,
  type PatologiaListItem,
  type PatologiaCreateData,
  type PatologiaUpdateData,
  type PatologiaFilters,
  type PaginatedResponse,
} from "@/services/patologias";

// Re-export types
export type {
  Patologia,
  PatologiaListItem,
  PatologiaCreateData,
  PatologiaUpdateData,
  PatologiaFilters,
} from "@/services/patologias";

// Query keys
export const patologiaKeys = {
  all: ["patologias"] as const,
  lists: () => [...patologiaKeys.all, "list"] as const,
  list: (filters: PatologiaFilters) => [...patologiaKeys.lists(), filters] as const,
  details: () => [...patologiaKeys.all, "detail"] as const,
  detail: (id: number) => [...patologiaKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated patologías
 */
export function usePatologias(filters: PatologiaFilters = {}) {
  return useQuery<PaginatedResponse<PatologiaListItem>>({
    queryKey: patologiaKeys.list(filters),
    queryFn: () => getPatologias(filters),
  });
}

/**
 * Hook to fetch a single patología
 */
export function usePatologia(id: number | null) {
  return useQuery<Patologia>({
    queryKey: patologiaKeys.detail(id!),
    queryFn: () => getPatologia(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a patología
 */
export function useCreatePatologia(options?: {
  onSuccess?: (data: Patologia) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatologiaCreateData) => createPatologia(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: patologiaKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Hook to update a patología
 */
export function useUpdatePatologia(options?: {
  onSuccess?: (data: Patologia) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PatologiaUpdateData }) =>
      updatePatologia(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: patologiaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patologiaKeys.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Hook to delete a patología (hard delete)
 */
export function useDeletePatologia(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deletePatologia(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patologiaKeys.lists() });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Hook to deactivate a patología (soft delete)
 */
export function useDeactivatePatologia(options?: {
  onSuccess?: (data: Patologia) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivatePatologia(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: patologiaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patologiaKeys.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Hook to activate a patología
 */
export function useActivatePatologia(options?: {
  onSuccess?: (data: Patologia) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activatePatologia(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: patologiaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patologiaKeys.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
